import React, { useEffect, useState } from "react";
import { Table, Button, Modal, Form, Input, Select, Space, message, Popconfirm } from "antd";
import { CheckOutlined, CloseOutlined, DownloadOutlined, DollarOutlined } from "@ant-design/icons";
import axios from "axios";
import useRequest from "../../hooks/useRequest";
import apiPath from "../../constants/apiPath";
import PageHeader from "../../components/ui/PageHeader";
import PageTransition from "../../components/ui/PageTransition";
import EmptyState from "../../components/ui/EmptyState";
import StatusTag from "../../components/ui/StatusTag";

// Mirrors the backend's constants/currencies.ts — keep in sync if that
// list changes.
const CURRENCY_SYMBOLS = { PHP: "₱", USD: "$", INR: "₹", EUR: "€", GBP: "£", AUD: "A$", SGD: "S$", JPY: "¥", CNY: "¥", CAD: "C$" };
const currencySymbol = (code) => CURRENCY_SYMBOLS[code] || `${code} `;

const Reimbursements = () => {
  const { request } = useRequest();
  const [requests, setRequests] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [filters, setFilters] = useState({});
  const [actionModal, setActionModal] = useState({ open: false, type: null, record: null });
  const [form] = Form.useForm();

  const fetchRequests = () => {
    const params = new URLSearchParams(filters).toString();
    request({
      url: `${apiPath.reimbursements}${params ? `?${params}` : ""}`,
      method: "GET",
      onSuccess: (res) => setRequests(res?.data?.data || []),
    });
  };

  const fetchDepartments = () => {
    request({ url: apiPath.departments, method: "GET", onSuccess: (res) => setDepartments(res?.data?.data || []) });
  };

  useEffect(() => {
    fetchDepartments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const onFilterChange = (changed) => {
    const next = { ...filters, ...changed };
    Object.keys(next).forEach((k) => (next[k] === undefined || next[k] === "" || next[k] === null) && delete next[k]);
    setFilters(next);
  };

  const openAction = (type, record) => {
    form.resetFields();
    setActionModal({ open: true, type, record });
  };

  const onSubmitAction = () => {
    form.validateFields().then((values) => {
      const { type, record } = actionModal;
      const urlMap = {
        approve: `${apiPath.reimbursements}/${record._id}/approve`,
        reject: `${apiPath.reimbursements}/${record._id}/reject`,
      };
      request({
        url: urlMap[type],
        method: "PUT",
        data: { adminComment: values.comment },
        onSuccess: () => {
          message.success("Reimbursement request updated");
          setActionModal({ open: false, type: null, record: null });
          fetchRequests();
        },
      });
    });
  };

  const onMarkReimbursed = (record) => {
    request({
      url: `${apiPath.reimbursements}/${record._id}/mark-reimbursed`,
      method: "PUT",
      onSuccess: () => {
        message.success("Marked as reimbursed");
        fetchRequests();
      },
    });
  };

  const onDownloadBill = async (record) => {
    const token = localStorage.getItem("i_ems_admin_token") || "";
    try {
      const response = await axios.get(`${apiPath.baseURL}${apiPath.reimbursementBillDownload(record._id)}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", record.billFileName || "bill");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      message.error("Could not download bill");
    }
  };

  const columns = [
    { title: "Employee", dataIndex: ["employee", "name"] },
    { title: "Department", dataIndex: ["employee", "department", "name"] },
    { title: "Category", dataIndex: "category" },
    { title: "Description", dataIndex: "description", ellipsis: true },
    {
      title: "Estimated",
      dataIndex: "estimatedAmount",
      render: (v, r) => `${currencySymbol(r.currency || "PHP")}${(v || 0).toLocaleString()}`,
    },
    {
      title: "Actual",
      dataIndex: "actualAmount",
      render: (v, r) => (v != null ? `${currencySymbol(r.currency || "PHP")}${v.toLocaleString()}` : "-"),
    },
    { title: "Status", dataIndex: "status", render: (s) => <StatusTag status={s} /> },
    { title: "Comment", dataIndex: "adminComment" },
    {
      title: "Actions",
      render: (_, record) => (
        <Space>
          {record.status === "pending" && (
            <>
              <Button size="small" type="primary" icon={<CheckOutlined />} onClick={() => openAction("approve", record)}>
                Approve
              </Button>
              <Button size="small" danger icon={<CloseOutlined />} onClick={() => openAction("reject", record)}>
                Reject
              </Button>
            </>
          )}
          {record.billStoredFileName && (
            <Button size="small" icon={<DownloadOutlined />} onClick={() => onDownloadBill(record)}>
              Bill
            </Button>
          )}
          {record.status === "bill_submitted" && (
            <Popconfirm title="Mark this reimbursement as paid?" onConfirm={() => onMarkReimbursed(record)}>
              <Button size="small" icon={<DollarOutlined />}>
                Mark Reimbursed
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <PageTransition>
      <PageHeader title="Reimbursements" subtitle="Review and process employee reimbursement requests" />
      <Space style={{ marginBottom: 16 }} wrap>
        <Select
          allowClear
          placeholder="Status"
          style={{ width: 170 }}
          value={filters.status}
          onChange={(v) => onFilterChange({ status: v })}
          options={[
            { value: "pending", label: "Pending" },
            { value: "approved", label: "Approved" },
            { value: "rejected", label: "Rejected" },
            { value: "bill_submitted", label: "Bill Submitted" },
            { value: "reimbursed", label: "Reimbursed" },
          ]}
        />
        <Select
          allowClear
          placeholder="Department"
          style={{ width: 200 }}
          value={filters.department}
          onChange={(v) => onFilterChange({ department: v })}
          options={departments.map((d) => ({ value: d._id, label: d.name }))}
        />
      </Space>
      <Table
        rowKey="_id"
        columns={columns}
        dataSource={requests}
        pagination={{ pageSize: 10 }}
        locale={{ emptyText: <EmptyState message="No reimbursement requests found" /> }}
      />

      <Modal
        title={actionModal.type === "approve" ? "Approve Reimbursement" : "Reject Reimbursement"}
        open={actionModal.open}
        onCancel={() => setActionModal({ open: false, type: null, record: null })}
        onOk={onSubmitAction}
        destroyOnClose
      >
        <Form layout="vertical" form={form}>
          <Form.Item name="comment" label="Comment (optional)">
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>
    </PageTransition>
  );
};

export default Reimbursements;
